import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatShoppingSeedPrice } from '../shared/shoppingSeedPricing';
import { resolveDraftNormalizationPreview } from '../shared/shoppingNormalization';

/**
 * DraftItemRow v1.0
 * Displays a single added item in the draft list with optional chips.
 */
const DraftItemRow = ({ item, onResolve, onRemove, onUpdate, onEdit, index, mode = 'edit' }) => {
    const isAmbiguous = item.resolveStatus === 'AMBIGUOUS';
    const isUnknown = item.resolveStatus === 'UNKNOWN';
    const showBrandField = Boolean(item.requiresBrand);
    const fieldGridClass = showBrandField ? 'grid-cols-4' : 'grid-cols-3';
    const [showPriceInfo, setShowPriceInfo] = useState(false);
    const isSummaryMode = mode === 'summary';
    const normalizationPreview = resolveDraftNormalizationPreview(item);

    if (isSummaryMode && !isAmbiguous && !isUnknown) {
        return (
            <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={onEdit}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEdit?.();
                    }
                }}
                className="mb-2 flex w-full cursor-pointer items-center justify-between rounded-2xl border border-gray-100 bg-white px-4 py-3 text-left transition-all hover:border-primary/30 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20 dark:hover:bg-gray-800/30"
            >
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                        <h4 className="truncate text-sm font-black uppercase tracking-tight dark:text-white">
                            {item.name}
                        </h4>
                    </div>
                    <p className="mt-1 text-[11px] font-bold text-gray-500 dark:text-gray-400">
                        {item.amount} {item.unit}
                        {item.brand ? ` · ${item.brand}` : ''}
                        {item.alternativeProduct ? ` · Alt: ${item.alternativeProduct}` : ''}
                        {item.price ? ` · ${formatShoppingSeedPrice(item.price)}` : ''}
                    </p>
                    {normalizationPreview?.state === 'resolved' && (
                        <p className="mt-1 text-[10px] font-bold text-amber-600 dark:text-amber-300">
                            {normalizationPreview.text}
                        </p>
                    )}
                </div>
                <div className="ml-3 flex items-center gap-2">
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                        Düzenle
                    </span>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove?.();
                        }}
                        className="p-1.5 text-gray-300 transition-colors hover:text-red-500"
                    >
                        <span className="material-symbols-outlined text-lg">close</span>
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`p-4 mb-3 border-2 rounded-2xl transition-all ${isAmbiguous ? 'border-amber-200 bg-amber-50/30 dark:border-amber-900/40 dark:bg-amber-900/10' : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900/20'}`}
        >
            <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isAmbiguous ? 'bg-amber-500 animate-pulse' : isUnknown ? 'bg-gray-400' : 'bg-green-500'}`} />
                            <h4 className="text-sm font-black dark:text-white uppercase tracking-tight">
                                {item.name}
                            </h4>
                        </div>

                        {isAmbiguous && (
                            <div className="mt-3">
                                <p className="text-[10px] text-amber-600 dark:text-amber-400 font-black uppercase mb-2">
                                    Hangi {item.name}? Seçiniz:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {item.candidates?.map((c) => (
                                        <button
                                            key={c.id}
                                            onClick={() => onResolve(c)}
                                            className="px-3 py-1.5 bg-white dark:bg-gray-800 border-2 border-amber-200 dark:border-amber-900/50 hover:border-amber-500 rounded-xl text-[10px] font-black uppercase transition-all active:scale-95"
                                        >
                                            {c.canonicalName}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={onRemove}
                        className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                    >
                        <span className="material-symbols-outlined text-lg">close</span>
                    </button>
                </div>

                {/* Edit Fields: Amount / Unit / Brand / Seed Price */}
                <div className={`grid ${fieldGridClass} gap-2 items-end`}>
                    {/* --- Amount (number) --- */}
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 px-1">
                            <span className="material-symbols-outlined text-xs text-gray-400 shrink-0">scale</span>
                            <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Miktar</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 h-9 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800 focus-within:border-primary/30 transition-all">
                            <input 
                                type="number"
                                min="0"
                                step="0.1"
                                value={item.amount || ''}
                                onChange={(e) => onUpdate({ amount: e.target.value })}
                                placeholder="0"
                                className="w-full bg-transparent text-[11px] font-bold text-gray-900 dark:text-gray-200 outline-none placeholder:text-gray-400"
                            />
                        </div>
                    </div>
                    {/* --- Unit (dropdown) --- */}
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 px-1">
                            <span className="material-symbols-outlined text-xs text-gray-400 shrink-0">straighten</span>
                            <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Birim</span>
                        </div>
                        <div className="flex items-center h-9 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800 focus-within:border-primary/30 transition-all overflow-hidden">
                            <select
                                value={item.unit || 'adet'}
                                onChange={(e) => onUpdate({ unit: e.target.value })}
                                className="w-full h-full px-2 bg-transparent text-[11px] font-black text-gray-900 dark:text-gray-200 outline-none cursor-pointer appearance-none uppercase"
                            >
                                <option value="adet">adet</option>
                                <option value="kg">kg</option>
                                <option value="gr">gr</option>
                                <option value="lt">lt</option>
                                <option value="ml">ml</option>
                                <option value="paket">paket</option>
                                <option value="demet">demet</option>
                                <option value="kutu">kutu</option>
                                <option value="koli">koli</option>
                            </select>
                        </div>
                    </div>

                    {showBrandField && (
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5 px-1">
                                <span className="material-symbols-outlined text-xs text-gray-400">label</span>
                                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Marka</span>
                            </div>
                            <div className="flex items-center gap-2 px-3 h-9 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800 focus-within:border-primary/30 transition-all">
                                <input 
                                    type="text" 
                                    value={item.brand || ''}
                                    onChange={(e) => onUpdate({ brand: e.target.value })}
                                    placeholder="-"
                                    className="w-full bg-transparent text-[11px] font-bold text-gray-900 dark:text-gray-200 outline-none placeholder:text-gray-400 uppercase"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2 px-1">
                            <div className="flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-xs text-gray-400">payments</span>
                                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                                    {item.priceAutoFilled ? 'Varsayılan Fiyat' : 'Fiyat'}
                                </span>
                            </div>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setShowPriceInfo((prev) => !prev)}
                                    onBlur={() => setTimeout(() => setShowPriceInfo(false), 120)}
                                    className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-[10px] font-black text-gray-600 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                    aria-label="Varsayılan fiyat bilgisi"
                                >
                                    i
                                </button>
                                {showPriceInfo && (
                                    <div className="absolute right-0 top-6 z-10 w-52 rounded-xl bg-gray-900 px-3 py-2 text-[10px] font-medium leading-4 text-white shadow-2xl dark:bg-black">
                                        Bu fiyatlar market verilerine göre alınan ortalama fiyatlardır. Kesin fiyatlar sizlerin alışveriş sonrasında girdiği fiyatlar sonrasında oluşacaktır.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 px-3 h-9 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800 transition-all">
                            <span className="text-[11px] font-bold text-gray-900 dark:text-gray-200">
                                {formatShoppingSeedPrice(item.price)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default DraftItemRow;
